import React, { useEffect, useMemo, useState } from "react";
import "./SdExecucaoTimer.css";

/* ===== Helpers ===== */
function isZeroLike(raw) {
  return (
    raw == null ||
    String(raw).trim() === "" ||
    String(raw).trim() === "00:00" ||
    String(raw).trim() === "00:00:00"
  );
}

function parseToSeconds(v) {
  if (v == null || v === "") return null;
  if (typeof v === "number" && Number.isFinite(v)) return Math.trunc(v) * 60;
  const s = String(v).trim();
  if (!s || s === "—" || s === "-") return null;
  const parts = s.split(":").map((n) => parseInt(n, 10));
  if (parts.some((x) => Number.isNaN(x))) return null;
  if (parts.length === 3) {
    const [h, m, s2] = parts;
    return h * 3600 + m * 60 + s2;
  }
  if (parts.length === 2) {
    const [h, m] = parts;
    return h * 3600 + m * 60;
  }
  return parts[0] * 60;
}

function parseToTsWithDay(value, refDay) {
  if (value == null) return null;
  if (value instanceof Date) {
    const t = value.getTime();
    return Number.isFinite(t) ? t : null;
  }
  let raw = String(value).trim();
  if (!raw || isZeroLike(raw)) return null;
  raw = raw.replace(/\.\d+$/, "");

  if (/^\d{10}$/.test(raw)) return parseInt(raw, 10) * 1000;
  if (/^\d{13}$/.test(raw)) return parseInt(raw, 10);

  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const [hh, mm, ss = "00"] = raw.split(":");
    const day = refDay || new Date().toISOString().slice(0, 10);
    const iso = `${day}T${String(hh).padStart(2, "0")}:${mm}:${ss}`;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }

  if (/^\d{4}-\d{2}-\d{2}\s+\d{1,2}:\d{2}(:\d{2})?$/.test(raw)) {
    const d = new Date(raw.replace(" ", "T"));
    return Number.isNaN(d.getTime()) ? null : d.getTime();
  }

  let d = new Date(raw);
  if (!Number.isNaN(d.getTime())) return d.getTime();
  d = new Date(raw.replace(" ", "T") + "Z");
  if (!Number.isNaN(d.getTime())) return d.getTime();
  return null;
}

function computeConsumedSeconds(pairs, nowTs) {
  let ms = 0;
  for (const { start, end } of pairs) {
    if (start == null) continue;
    const e = end == null ? nowTs : end;
    if (e > start) ms += e - start;
  }
  return Math.floor(ms / 1000);
}

function formatHMFromSeconds(totalSec) {
  const s = Math.max(0, Math.floor(totalSec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/* ===== Hook ===== */
function useExecucaoCountdown({
  day,
  tempo_do_intervalo,
  tempo_total,
  primeiro_inicio,
  primeiro_fim,
  segundo_inicio,
  segundo_fim,
  terceiro_inicio,
  terceiro_fim,
  nowTs: nowTsExternal,
  allowOverrun = true,
}) {
  const metaSeconds = useMemo(() => {
    const target = tempo_do_intervalo ?? tempo_total;
    const v = parseToSeconds(target);
    return v == null ? null : Math.max(0, v);
  }, [tempo_do_intervalo, tempo_total]);

  const pairs = useMemo(() => {
    const p1s = parseToTsWithDay(primeiro_inicio, day);
    const p1e = parseToTsWithDay(primeiro_fim, day);
    const p2s = parseToTsWithDay(segundo_inicio, day);
    const p2e = parseToTsWithDay(segundo_fim, day);
    const p3s = parseToTsWithDay(terceiro_inicio, day);
    const p3e = parseToTsWithDay(terceiro_fim, day);
    return [
      { start: p1s, end: p1e },
      { start: p2s, end: p2e },
      { start: p3s, end: p3e },
    ];
  }, [
    primeiro_inicio,
    primeiro_fim,
    segundo_inicio,
    segundo_fim,
    terceiro_inicio,
    terceiro_fim,
    day,
  ]);

  const [nowTsInternal, setNowTsInternal] = useState(Date.now());
  const nowTs = nowTsExternal ?? nowTsInternal;

  const status = useMemo(() => {
    let lastOpen = -1;
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i].start != null) lastOpen = i;
    }
    if (lastOpen === -1) return "waiting";
    const last = pairs[lastOpen];
    return last.end == null ? "running" : "paused";
  }, [pairs]);

  useEffect(() => {
    if (nowTsExternal != null) return;
    if (status !== "running") return;
    const id = setInterval(() => setNowTsInternal(Date.now()), 1000);
    return () => clearInterval(id);
  }, [status, nowTsExternal]);

  return useMemo(() => {
    if (metaSeconds == null || metaSeconds <= 0 || pairs[0].start == null) {
      return { status, metaSeconds, countdown: null, exhausted: false };
    }
    const consumed = computeConsumedSeconds(pairs, nowTs);
    let remaining = metaSeconds - consumed;
    const exhausted = remaining <= 0;
    if (!allowOverrun) remaining = Math.max(0, remaining);
    return { status, metaSeconds, countdown: remaining, exhausted };
  }, [pairs, nowTs, metaSeconds, status, allowOverrun]);
}

/* ===== Componente ===== */
export default function SdExecucaoTimer(props) {
  const { status, metaSeconds, countdown, exhausted } = useExecucaoCountdown(props);

  // ✅ Sem placeholder: não renderiza nada enquanto não iniciou
  if (countdown == null) return null;

  const neg = countdown < 0;
  const label = neg
    ? `-${formatHMFromSeconds(Math.abs(countdown))}`
    : formatHMFromSeconds(countdown);

  const title =
    status === "paused"
      ? `Execução pausada — Restante: ${label}`
      : exhausted
      ? `Execução estourada: ${label}`
      : `Execução em andamento: ${label}`;

  return (
    <span
      className={`exec-timer ${neg ? "eta--expired" : ""}`}
      title={title}
      data-status={status}
      data-negative={neg ? "1" : "0"}
      data-meta={metaSeconds ?? ""}
    >
      {label}
    </span>
  );
}
